import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddLinkRounded from '@mui/icons-material/AddLinkRounded';
import {
  EmptyState, ListTable, Panel, StatusChip, ToolbarButton, type ListColumn, type StatusTone,
} from '../../../ui-kit';
import { fetchMcIntegrations, type McIntegration } from '../../../services/missedCallService';
import { useLive } from '../rocco/live';
import { AgencyZoomMark, TwilioMark, WhatsAppMark } from './logos';

function stateOf(row: McIntegration): { tone: StatusTone; label: string } {
  if (!row.connected) return { tone: 'neutral', label: 'Not connected' };
  if (row.error) return { tone: 'warning', label: 'Needs attention' };
  return { tone: 'good', label: 'Connected' };
}

const LOGOS: Record<McIntegration['id'], React.ReactNode> = {
  phone: <TwilioMark />,
  whatsapp: <WhatsAppMark />,
  agencyzoom: <AgencyZoomMark />,
};

/** Other systems that take the same place as the ones in use. */
const ALSO_WORKS_WITH: Record<McIntegration['id'], string> = {
  phone: 'Any number the agency forwards to it, from DYL, RingCentral or a desk phone',
  whatsapp: 'Text messages or email, once set up',
  agencyzoom: 'HubSpot, Salesforce or a shared calendar',
};

/**
 * What the Missed Call Follow-up agent is joined to, as it is right now: the
 * number clients call, WhatsApp for the messages, and AgencyZoom for the
 * callbacks. AgencyZoom is added on the Connectors page, like any connector.
 */
export default function IntegrationsPage() {
  const navigate = useNavigate();
  const { data, error, loading } = useLive(fetchMcIntegrations, 30_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<McIntegration>[] = [
    {
      key: 'name',
      header: 'System',
      grow: 1.2,
      minWidth: 200,
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>{LOGOS[row.id]}</Box>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{row.detail}</Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      key: 'role',
      header: 'Does',
      width: 160,
      render: (row) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{row.role}</Typography>,
    },
    {
      key: 'state',
      header: 'State',
      width: 220,
      render: (row) => (
        row.id === 'agencyzoom' && !row.connected ? (
          <ToolbarButton variant="outlined" icon={<AddLinkRounded />} onClick={() => navigate('/connectors')}>
            Connect AgencyZoom
          </ToolbarButton>
        ) : <StatusChip {...stateOf(row)} />
      ),
    },
    {
      key: 'activity',
      header: 'Recently',
      grow: 2,
      minWidth: 220,
      render: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.activity}</Typography>
          {row.error ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{row.error}</Typography>
          ) : (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              Also works with {ALSO_WORKS_WITH[row.id]}
            </Typography>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Panel title="Connected systems" subtitle="Calls in, messages out, callbacks on the calendar · as they are now">
      <ListTable
        columns={columns}
        rows={data?.integrations ?? []}
        getRowKey={(row) => row.id}
        loading={loading}
        emptyMessage="Nothing connected."
      />
    </Panel>
  );
}
